"use client"

import { useMemo, useState } from "react"
import { ThumbsUp, ThumbsDown, FileSpreadsheet, FileText, Image } from "lucide-react"
import ReactMarkdown from "react-markdown"
import rehypeRaw from "rehype-raw"
import rehypeSanitize, { defaultSchema } from "rehype-sanitize"
import type { Options as SanitizeSchema } from "rehype-sanitize"
import remarkGfm from "remark-gfm"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism"
import { Message } from "./types"
import { FeedbackDialog } from "./FeedbackDialog"
import { FileDownloadCard } from "./FileDownloadCard"
import { ChoiceCard, parseChoicesBlock } from "./ChoiceCard"
import { GeneratedImage, isImageFilename } from "./GeneratedImage"
import { PollingImage } from "./PollingImage"
import { AgentFace } from "@/components/AgentFace"
import type { AgentFaceInfo } from "@/hooks/useAgentProfile"

interface ExtractedFile {
  url: string
  filename: string
}

/** decodeURIComponent that won't throw on partial percent-encoding during streaming */
function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

/**
 * Check if a presigned S3 URL is complete (its signature query has fully arrived).
 *
 * Accepts BOTH signing schemes the backend can emit:
 *   - SigV4: `X-Amz-Signature` (alongside X-Amz-Algorithm / X-Amz-Credential)
 *   - SigV2 (STS temporary creds): `AWSAccessKeyId` + `Signature`
 * Earlier this only checked for `X-Amz-`, so a SigV2 URL was treated as "incomplete" and the
 * generated image never rendered. Parsing with `URL`/`searchParams` and requiring the actual
 * signature param (not just any substring) also avoids treating a mid-stream URL — where only
 * the leading params have arrived — as complete.
 */
export function isCompleteS3Url(url: string): boolean {
  try {
    // OCI の同一オリジンプロキシ URL は相対（/os/…）で来る。相対でも searchParams を
    // 読めるようダミー base を与える（絶対 URL のときは base は無視される）。
    const q = new URL(url, "https://placeholder.invalid").searchParams
    // SigV4 signature is always the final query param and a 64-char lowercase hex digest, so a
    // full match means the whole URL has arrived (not a mid-stream prefix with an empty value).
    const v4 = q.get("X-Amz-Signature")
    if (v4 && /^[0-9a-f]{64}$/.test(v4)) return true
    // SigV2 (STS temp creds): require the key id, a non-empty signature, and Expires together.
    const v2 = q.get("Signature")
    return Boolean(v2 && q.has("AWSAccessKeyId") && q.has("Expires"))
  } catch {
    // Not yet a parseable URL (still streaming in) — treat as incomplete.
    return false
  }
}

/**
 * Extract S3 presigned URLs from content before markdown rendering.
 * Long presigned URLs break markdown link parsing, so we extract them
 * and render as FileDownloadCards separately.
 * Safe during streaming — incomplete URLs are left in place.
 */
export function extractS3Files(content: string): { cleanContent: string; files: ExtractedFile[] } {
  const files: ExtractedFile[] = []

  // 1. Extract markdown links containing S3 generated URLs: [text](s3-url)
  //    OCI: 同一オリジンプロキシの相対 URL（/os/…/generated/…）も拾う。
  let clean = content.replace(
    /\[([^\]]*)\]\(((?:https?:\/\/[^)]*amazonaws\.com[^)]*|\/os\/[^)]*)\/generated\/[^)]+)\)/g,
    (_match, _text, url) => {
      if (!isCompleteS3Url(url)) return _match // leave incomplete URLs as-is
      const pathAfterGenerated = url.split("/generated/")[1]?.split("?")[0] || ""
      const filename = safeDecode(pathAfterGenerated.split("/").pop() || "file")
      files.push({ url, filename })
      return ""
    }
  )

  // 2. Extract raw S3 URLs (not wrapped in markdown link syntax). The path+query after
  //    `/generated/` uses an explicit URL-safe ALLOWLIST so the match stops cleanly at any
  //    following text (e.g. a directly-appended Japanese char) instead of swallowing it; it
  //    keeps SigV2/SigV4 query chars (& % = / ? . - _ ~) and matches any generated object
  //    (status.json, openai.png, PDFs, …).
  clean = clean.replace(
    /(?:https?:\/\/[^\s"'<>`)\]]*amazonaws\.com(?=[/:])[^\s"'<>`)\]]*|\/os\/[^\s"'<>`)\]]*?)\/generated\/[A-Za-z0-9%&=\-._~/?]+/g,
    (url) => {
      // Strip trailing punctuation — ASCII and Japanese (。、）！？」』 など) — so a presigned
      // URL written right before punctuation isn't passed on corrupted.
      const trimmed = url.replace(/[.,;:!?)\]"'`<>。、）」』！？]+$/u, "")
      if (!isCompleteS3Url(trimmed)) return url // leave incomplete URLs as-is
      const pathAfterGenerated = trimmed.split("/generated/")[1]?.split("?")[0] || ""
      const filename = safeDecode(pathAfterGenerated.split("/").pop() || "file")
      if (!files.some((f) => f.url === trimmed)) {
        files.push({ url: trimmed, filename })
      }
      return ""
    }
  )

  // 3. Clean up leftover formatting artifacts
  clean = clean
    .replace(/\*\*\s*\*\*/g, "") // Empty bold markers
    .replace(/\n{3,}/g, "\n\n") // Excessive blank lines
    .trim()

  return { cleanContent: clean, files }
}

/**
 * アシスタントの Markdown を rehypeRaw で生 HTML 展開した後に通すサニタイズ用スキーマ
 * （XSS 対策・担当F）。GitHub 準拠の defaultSchema をベースに、rehypeRaw が混入させ得る
 * 危険要素を明示的に上乗せで落とす:
 *   - <script>/<style>/<iframe>/<object>/<embed> はタグごと（中身のテキストも含めて）除去。
 *     defaultSchema のデフォルト挙動は「許可外タグは剥がすが中身は残す」なので、<style> の
 *     生 CSS テキストなどが素通しの本文として画面に出てしまう。strip に足して中身ごと消す。
 *   - on* 属性・javascript: スキームは defaultSchema がそもそもホワイトリスト方式
 *     （許可した属性・プロトコルしか通さない）なので、追加設定なしで標準的に落ちる。
 *
 * このアプリの Markdown 実表現（見出し・強調・リスト・table・code の language-* クラス
 * ["```choices" 選択肢カード／シンタックスハイライト共に "language-xxx" 形式]・
 * details/summary・リンク・画像）は defaultSchema の GitHub 互換範囲にすべて収まっており、
 * tagNames/attributes 自体の追加拡張は不要（frontend/src/lib/avatar.ts の
 * sanitizeAvatarSvg と同じ「defaultSchema ベース＋最小限の明示追加」という方針を踏襲）。
 */
const chatMarkdownSanitizeSchema: SanitizeSchema = {
  ...defaultSchema,
  strip: [...(defaultSchema.strip || []), "style", "iframe", "object", "embed"],
}

interface ChatMessageProps {
  message: Message
  sessionId: string
  agentFace?: AgentFaceInfo
  onFeedbackSubmit: (feedbackType: "positive" | "negative", comment: string) => Promise<void>
  // 選択肢カード（```choices ブロック）のタップを発言として送信する（B2・2026-07-05）。
  // 未配線（履歴閲覧など送信できない文脈）ではカードは表示のみになる。
  onSendChoice?: (text: string) => void
}

export function ChatMessage({
  message,
  sessionId: _sessionId,
  agentFace,
  onFeedbackSubmit,
  onSendChoice,
}: ChatMessageProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedFeedbackType, setSelectedFeedbackType] = useState<"positive" | "negative">(
    "positive"
  )
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)

  const { cleanContent, files } = useMemo(
    () =>
      message.role === "assistant"
        ? extractS3Files(message.content)
        : { cleanContent: message.content, files: [] },
    [message.content, message.role]
  )

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  }

  const handleFeedbackClick = (type: "positive" | "negative") => {
    setSelectedFeedbackType(type)
    setIsDialogOpen(true)
  }

  const handleFeedbackSubmit = async (comment: string) => {
    await onFeedbackSubmit(selectedFeedbackType, comment)
    setFeedbackSubmitted(true)
  }

  const isAssistant = message.role === "assistant"
  return (
    <div className={`flex ${isAssistant ? "justify-start gap-2" : "justify-end"}`}>
      {/* アシスタントの回答は、エージェントの顔から吹き出しが出る形にする */}
      {isAssistant && (
        <div className="shrink-0 mt-1">
          <AgentFace
            avatarUrl={agentFace?.avatarUrl}
            icon={agentFace?.icon}
            size={32}
          />
        </div>
      )}
      <div className={`flex flex-col min-w-0 ${isAssistant ? "items-start" : "items-end"}`}>
      <div
        className={`max-w-[80%] p-3 rounded-lg break-words ${
          message.role === "user"
            ? "bg-primary text-primary-foreground rounded-br-none whitespace-pre-wrap"
            : "bg-gray-100 text-gray-800 rounded-bl-none"
        }`}
      >
        {/* Attachment badges for user messages */}
        {message.role === "user" && message.attachments && message.attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {message.attachments.map((att, i) => {
              const ext = att.filename.split(".").pop()?.toLowerCase() || ""
              const isImage = ["png", "jpg", "jpeg", "gif", "webp"].includes(ext)
              const isSpreadsheet = ["xlsx", "csv"].includes(ext)
              const AttIcon = isImage ? Image : isSpreadsheet ? FileSpreadsheet : FileText
              const iconColor = isImage ? "text-purple-300" : isSpreadsheet ? "text-green-300" : "text-blue-300"
              return (
                <span key={i} className="inline-flex items-center gap-1 bg-white/15 rounded px-1.5 py-0.5 text-xs text-white/90">
                  <AttIcon size={12} className={iconColor} />
                  {att.filename}
                </span>
              )
            })}
          </div>
        )}
        {message.role === "assistant" ? (
          <div className="markdown-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              // rehypeRaw で生 HTML をパースした「後」に rehypeSanitize を通す（順序が
              // 逆だとサニタイズが効かない＝ rehypeRaw が展開した危険タグが素通りする）。
              rehypePlugins={[rehypeRaw, [rehypeSanitize, chatMarkdownSanitizeSchema]]}
              components={{
                code({ className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || "")
                  // 選択肢カード（B2）: ```choices ブロックはタップで答えられるカードとして
                  // 描画する。解析できない形式は通常のコードブロックに落ちる＝番号付き
                  // テキストとして読める（フォールバック）。
                  if (match && match[1] === "choices") {
                    const spec = parseChoicesBlock(String(children))
                    if (spec) {
                      return <ChoiceCard spec={spec} onSend={onSendChoice} />
                    }
                  }
                  return match ? (
                    <SyntaxHighlighter style={oneDark} language={match[1]} PreTag="div">
                      {String(children).replace(/\n$/, "")}
                    </SyntaxHighlighter>
                  ) : (
                    <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-sm" {...props}>
                      {children}
                    </code>
                  )
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                a: ((props: any) => {
                  const { href, children } = props
                  if (href && (href.includes("amazonaws.com") || href.startsWith("/os/")) && href.includes("/generated/") && isCompleteS3Url(href)) {
                    // Async image job: poll status.json and render the image when ready.
                    if (href.includes("/status.json")) {
                      return <PollingImage statusUrl={href} />
                    }
                    const pathAfterGenerated = href.split("/generated/")[1]?.split("?")[0] || ""
                    const filename = safeDecode(pathAfterGenerated.split("/").pop() || "file")
                    // Direct link to a completed image (e.g. the model re-showing a manga page):
                    // show the picture inline, not a download card.
                    if (isImageFilename(filename)) {
                      return <GeneratedImage href={href} filename={filename} />
                    }
                    return <FileDownloadCard href={href} filename={filename} />
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  )
                }) as React.ComponentType,
              }}
            >
              {cleanContent}
            </ReactMarkdown>
            {files.map((file, i) =>
              file.url.includes("/status.json") ? (
                <PollingImage key={i} statusUrl={file.url} />
              ) : isImageFilename(file.filename) ? (
                <GeneratedImage key={i} href={file.url} filename={file.filename} />
              ) : (
                <FileDownloadCard key={i} href={file.url} filename={file.filename} />
              )
            )}
          </div>
        ) : (
          message.content
        )}
      </div>

      {/* Timestamp and Feedback buttons for assistant messages */}
      <div className="flex items-center gap-2 mt-1 px-1">
        <div className="text-xs text-gray-500">{formatTime(message.timestamp)}</div>

        {/* Show feedback buttons only for assistant messages with content */}
        {message.role === "assistant" && message.content && (
          <div className="flex items-center gap-1 ml-2">
            <button
              onClick={() => handleFeedbackClick("positive")}
              disabled={feedbackSubmitted}
              className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="良い回答"
              title="良い回答"
            >
              <ThumbsUp size={14} />
            </button>
            <button
              onClick={() => handleFeedbackClick("negative")}
              disabled={feedbackSubmitted}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="悪い回答"
              title="悪い回答"
            >
              <ThumbsDown size={14} />
            </button>
            {feedbackSubmitted && (
              <span className="text-xs text-gray-500 ml-1">フィードバックありがとうございます！</span>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Feedback Dialog */}
      <FeedbackDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleFeedbackSubmit}
        feedbackType={selectedFeedbackType}
      />
    </div>
  )
}
